/**
 * Home page category list.
 *
 * Customers browse two kinds of entries, both sourced from admin Trade Categories:
 *   - Trade Category (41)      → destination is the Trade Category
 *   - Public-Facing Name (37)  → destination is its linked Trade Category → Subcategory
 *
 * Icons reuse `assets/img/services/latest` SVGs; the same filenames are stored on the
 * admin records so uploaded icons can replace them later.
 */
import { buildSampleTradeCategories } from '../AdminDashboard/TradeCategories/tradeCategoriesDummyData';
import {
  slugifyTradeCategoryName,
  TRADE_CATEGORY_ICON_BY_FILE,
} from '../../data/tradeCategoryIconMap';

/**
 * Display-only stand-ins for Trade Categories with no artwork yet. The admin record
 * keeps an empty `iconFileName` so an uploaded icon still takes over.
 */
const TRADE_CATEGORY_ICON_FALLBACKS = {};

export const CATEGORY_ENTRY_TYPE = {
  TRADE_CATEGORY: 'trade_category',
  PUBLIC_FACING_NAME: 'public_facing_name',
};

/**
 * Home category tiles that show with no icon (missing artwork).
 * Names must match `businessCategoryName` exactly.
 */
export const HOME_CATEGORIES_WITHOUT_ICONS = new Set([
  'Bathroom Renovations',
  'Builders',
  'Building Drafting',
  'Concrete Grinding',
  'Epoxy Flooring',
  'Garage Doors',
  'Kitchen Renovations',
  'Polished Concrete',
  'Restumping & Underpinning',
  'Roof Painting',
  'Roof Plumbing',
  'Roof Restoration',
  'Security Systems',
  'Turf Installation',
  'Underfloor Heating',
  'Wallpapering',
  'Leak Repairs',
]);

const wideName = (name) => name.length > 12 || name.includes(' ');

function buildHomeCategories() {
  const entries = [];
  const seenSlugs = new Set();

  const push = (entry, fallbackIconFileName) => {
    if (seenSlugs.has(entry.slug)) return;
    seenSlugs.add(entry.slug);
    const withoutIcon = HOME_CATEGORIES_WITHOUT_ICONS.has(
      entry.businessCategoryName,
    );
    const displayIcon = withoutIcon
      ? ''
      : entry.iconFileName || fallbackIconFileName || '';
    entries.push({
      ...entry,
      id: entries.length + 1,
      iconFileName: withoutIcon ? '' : entry.iconFileName || '',
      categoryImage: TRADE_CATEGORY_ICON_BY_FILE[displayIcon] || '',
      imgclassName: 'cursor-pointer w-[80px] h-[80px]',
      textclassName: wideName(entry.businessCategoryName)
        ? 'text-center w-[70px] md:w-[90px]'
        : 'text-center w-[70px]',
    });
  };

  buildSampleTradeCategories().forEach((category) => {
    const categoryIcon =
      category.iconFileName || TRADE_CATEGORY_ICON_FALLBACKS[category.id] || '';

    push(
      {
        entryType: CATEGORY_ENTRY_TYPE.TRADE_CATEGORY,
        businessCategoryName: category.name,
        slug: slugifyTradeCategoryName(category.name),
        iconFileName: category.iconFileName || '',
        tradeCategoryId: category.id,
        tradeCategoryName: category.name,
        subcategoryId: '',
        subcategoryName: '',
      },
      categoryIcon,
    );

    (category.publicCategories || []).forEach((pc) => {
      push(
        {
          entryType: CATEGORY_ENTRY_TYPE.PUBLIC_FACING_NAME,
          businessCategoryName: pc.name,
          slug: slugifyTradeCategoryName(pc.name),
          iconFileName: pc.iconFileName || '',
          publicCategoryId: pc.id,
          tradeCategoryId: category.id,
          tradeCategoryName: category.name,
          subcategoryId: pc.sourceTradeSubcategoryId || '',
          subcategoryName: pc.sourceTradeSubcategoryName || '',
        },
        categoryIcon,
      );
    });
  });

  return entries;
}

export const businessCategories = buildHomeCategories();
